import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  ArcElement,
  Tooltip,
} from 'chart.js';
import type { LucideIcon } from 'lucide-react';
import {
  ShieldAlert,
  TicketCheck,
  Timer,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { DepartmentRole } from '@21k/shared';

import { getApiErrorMessage, getDashboard, listDepartments, listTeams } from '../../api/client';
import type { DashboardPayload, DepartmentSummary, TeamListItem } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getChartColors,
  barChartOptions,
  doughnutChartOptions,
  horizontalBarOptions,
  lineChartOptions,
} from '../../utils/chartTheme';
import { isSuperAdmin } from '../../utils/permissions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

function KpiCard({
  label,
  value,
  suffix,
  icon: Icon,
  iconClass,
}: {
  label: string;
  value: number | string;
  suffix?: string;
  icon: LucideIcon;
  iconClass: string;
}) {
  return (
    <div className="stat-card">
      <span className={`stat-card-icon ${iconClass}`} aria-hidden>
        <Icon size={16} strokeWidth={2} />
      </span>
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {value}
        {suffix}
      </div>
    </div>
  );
}

function useChartThemeKey() {
  const { resolvedTheme } = useTheme();
  return resolvedTheme;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [departmentId, setDepartmentId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const themeKey = useChartThemeKey();
  const chartColors = useMemo(() => getChartColors(), [themeKey]);

  const isAdminView = data?.view === 'admin';
  const isDeptAdminView = data?.view === 'department_admin';

  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin(user.role)) {
      void listDepartments().then(setDepartments).catch(() => undefined);
      return;
    }
    const adminDepts = user.departmentMemberships
      .filter((m) => m.role === DepartmentRole.DEPARTMENT_ADMIN)
      .map((m) => m.department);
    setDepartments(adminDepts);
    void Promise.all(adminDepts.map((d) => listTeams(d.id)))
      .then((lists) => setTeams(lists.flat()))
      .catch(() => undefined);
  }, [user]);

  useEffect(() => {
    setLoading(true);
    getDashboard({
      departmentId: departmentId || undefined,
      teamId: teamId || undefined,
    })
      .then(setData)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [departmentId, teamId]);

  const lineOpts = useMemo(() => lineChartOptions(), [themeKey]);
  const barOpts = useMemo(() => barChartOptions(), [themeKey]);
  const hBarOpts = useMemo(() => horizontalBarOptions(), [themeKey]);
  const donutOpts = useMemo(() => doughnutChartOptions(), [themeKey]);

  const volumeChart = useMemo(() => {
    if (!data) return null;
    const days = data.volumeTrend.length;
    const label =
      data.view === 'agent' ? '14-day volume' : '30-day volume';
    return {
      labels: data.volumeTrend.map((d) => d.date.slice(5)),
      datasets: [
        {
          label: 'Created',
          data: data.volumeTrend.map((d) => d.created),
          borderColor: chartColors.primary,
          backgroundColor: chartColors.primary,
          tension: 0.3,
        },
        {
          label: 'Resolved',
          data: data.volumeTrend.map((d) => d.resolved),
          borderColor: chartColors.success,
          backgroundColor: chartColors.success,
          tension: 0.3,
        },
      ],
      meta: label,
      compact: days <= 14,
    };
  }, [data, chartColors]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">
            {data?.view === 'agent'
              ? 'Your ticket workload and SLA health'
              : data?.view === 'department_admin'
                ? 'Department performance overview'
                : 'Platform-wide ticket analytics'}
          </p>
        </div>
        <div className="dashboard-filters">
          {isAdminView && departments.length > 0 && (
            <select
              className="form-control"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
          {isDeptAdminView && teams.length > 0 && (
            <select
              className="form-control"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div className="loading">Loading dashboard…</div>}

      {!loading && data && (
        <>
          <div className="stat-grid dashboard-kpis">
            <KpiCard
              label={data.view === 'agent' ? 'My open tickets' : 'Total open'}
              value={data.kpis.openTickets}
              icon={TicketCheck}
              iconClass="stat-card-icon-blue"
            />
            <KpiCard
              label={data.view === 'agent' ? 'My SLA compliance' : 'SLA compliance'}
              value={data.kpis.slaCompliancePct}
              suffix="%"
              icon={ShieldAlert}
              iconClass="stat-card-icon-green"
            />
            <KpiCard
              label={data.view === 'agent' ? 'My avg resolution' : 'Avg resolution time'}
              value={data.kpis.avgResolutionHours}
              suffix="h"
              icon={Timer}
              iconClass="stat-card-icon-amber"
            />
            <KpiCard
              label={data.view === 'agent' ? 'My escalated' : 'Total escalated'}
              value={data.kpis.escalatedCount}
              icon={TrendingUp}
              iconClass="stat-card-icon-red"
            />
          </div>

          <div className={`dashboard-charts ${data.view === 'agent' ? 'agent-charts' : ''}`}>
            {data.view !== 'agent' && (
              <>
                <div className="card card-body chart-card">
                  <h3>Open tickets by department</h3>
                  <div className="chart-box">
                    <Bar
                      key={`dept-${themeKey}`}
                      options={barOpts}
                      data={{
                        labels: data.departmentComparison.map((d) => d.name),
                        datasets: [
                          {
                            label: 'Open',
                            data: data.departmentComparison.map((d) => d.open),
                            backgroundColor: chartColors.primary,
                          },
                        ],
                      }}
                    />
                  </div>
                </div>
                <div className="card card-body chart-card">
                  <h3>Ticket volume trend</h3>
                  <div className="chart-box">
                    {volumeChart && (
                      <Line key={`vol-${themeKey}`} options={lineOpts} data={volumeChart} />
                    )}
                  </div>
                </div>
                <div className="card card-body chart-card">
                  <h3>Channel breakdown</h3>
                  <div className="chart-box">
                    <Bar
                      key={`ch-${themeKey}`}
                      options={hBarOpts}
                      data={{
                        labels: data.channelBreakdown.map((c) => c.channel),
                        datasets: [
                          {
                            label: 'Tickets',
                            data: data.channelBreakdown.map((c) => c.count),
                            backgroundColor: data.channelBreakdown.map(
                              (_, i) => chartColors.channels[i % chartColors.channels.length],
                            ),
                          },
                        ],
                      }}
                    />
                  </div>
                </div>
                <div className="card card-body chart-card">
                  <h3>SLA compliance trend</h3>
                  <div className="chart-box">
                    <Line
                      key={`sla-${themeKey}`}
                      options={lineOpts}
                      data={{
                        labels: data.slaTrend.map((w) => w.week),
                        datasets: [
                          {
                            label: 'Compliance %',
                            data: data.slaTrend.map((w) => w.compliancePct),
                            borderColor: chartColors.warning,
                            backgroundColor: chartColors.warning,
                            tension: 0.3,
                          },
                        ],
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            {data.view === 'agent' && data.statusDonut && (
              <div className="card card-body chart-card">
                <h3>My ticket status</h3>
                <div className="chart-box chart-box-sm">
                  <Doughnut
                    key={`donut-${themeKey}`}
                    options={donutOpts}
                    data={{
                      labels: data.statusDonut.map((s) => s.status),
                      datasets: [
                        {
                          data: data.statusDonut.map((s) => s.count),
                          backgroundColor: chartColors.channels,
                        },
                      ],
                    }}
                  />
                </div>
              </div>
            )}
            {data.view === 'agent' && volumeChart && (
              <div className="card card-body chart-card">
                <h3>My volume trend</h3>
                <div className="chart-box chart-box-sm">
                  <Line key={`agent-vol-${themeKey}`} options={lineOpts} data={volumeChart} />
                </div>
              </div>
            )}
          </div>

          {data.alerts.length > 0 && (
            <div className="card card-body alert-panel">
              <h3>Alerts</h3>
              <div className="dashboard-alert-list">
                {data.alerts.map((a) => (
                  <div key={a.ticketId} className="alert-item">
                    <Link className="alert-ticket-id" to={`/tickets/${a.ticketId}`}>
                      {a.title}
                    </Link>
                    <span className="alert-label">{a.reason}</span>
                    <span className="alert-dept">{a.departmentName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.departmentPerformance && data.departmentPerformance.length > 0 && (
            <div className="card card-body">
              <h3>Department performance</h3>
              <div className="table-wrap">
                <table className="data-table data-table-performance">
                  <thead>
                    <tr>
                      <th>Department</th>
                      <th>Open</th>
                      <th>Resolved</th>
                      <th>Avg time</th>
                      <th>SLA %</th>
                      <th>Escalated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.departmentPerformance.map((row) => (
                      <tr key={row.departmentId}>
                        <td>{row.name}</td>
                        <td>{row.open}</td>
                        <td>{row.resolved}</td>
                        <td className="col-tabular">{row.avgResolutionHours}h</td>
                        <td>{row.slaCompliancePct}%</td>
                        <td>{row.escalated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.agentPerformance && data.agentPerformance.length > 0 && (
            <div className="card card-body">
              <h3>Agent performance</h3>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Workload</th>
                      <th>Open</th>
                      <th>Resolved</th>
                      <th>Avg time</th>
                      <th>SLA %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agentPerformance.map((row) => (
                      <tr key={row.userId}>
                        <td>{row.name}</td>
                        <td>
                          <div className="workload-bar">
                            <div
                              className="workload-fill"
                              style={{ width: `${row.workloadPct}%` }}
                            />
                          </div>
                          <span className="workload-label">{row.open}/15</span>
                        </td>
                        <td>{row.open}</td>
                        <td>{row.resolved}</td>
                        <td>{row.avgResolutionHours}h</td>
                        <td>{row.slaCompliancePct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
